import * as React from "react";
import {
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
} from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";

interface RichTextFieldProps {
  label: string;
  value?: string | null;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: boolean;
  helperText?: React.ReactNode;
  placeholder?: string;
  minHeight?: number;
  fullWidth?: boolean;
}

const hasHtmlMarkup = (value: string) => /<\/?[a-z][\s\S]*>/i.test(value);

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const toEditorHtml = (value: string) => {
  if (!value) return "";
  if (hasHtmlMarkup(value)) return value;
  return escapeHtml(value).replace(/\n/g, "<br>");
};

const normalizeStoredHtml = (html: string) => {
  const container = document.createElement("div");
  container.innerHTML = html
    .replace(/<b(\s|>)/gi, "<strong$1")
    .replace(/<\/b>/gi, "</strong>");

  const plainText = (container.textContent || "").replace(/\u00a0/g, " ").trim();
  if (!plainText) {
    return "";
  }

  return container.innerHTML;
};

const RichTextField = ({
  label,
  value = "",
  onChange,
  onBlur,
  error = false,
  helperText,
  placeholder,
  minHeight = 140,
  fullWidth = true,
}: RichTextFieldProps) => {
  const editorRef = React.useRef<HTMLDivElement | null>(null);
  const lastHtmlRef = React.useRef<string>("");

  const syncEditorHtml = React.useCallback((nextValue: string) => {
    const nextHtml = toEditorHtml(nextValue);
    lastHtmlRef.current = nextHtml;

    if (editorRef.current && editorRef.current.innerHTML !== nextHtml) {
      editorRef.current.innerHTML = nextHtml;
    }
  }, []);

  React.useEffect(() => {
    syncEditorHtml(value || "");
  }, [syncEditorHtml, value]);

  const emitValue = React.useCallback(() => {
    if (!editorRef.current) return;
    const normalized = normalizeStoredHtml(editorRef.current.innerHTML);
    if (normalized !== lastHtmlRef.current) {
      lastHtmlRef.current = normalized;
      onChange(normalized);
    }
  }, [onChange]);

  const handleBold = React.useCallback(() => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    document.execCommand("styleWithCSS", false, "false");
    document.execCommand("bold");
    emitValue();
  }, [emitValue]);

  const handlePaste = React.useCallback(
    (event: React.ClipboardEvent<HTMLDivElement>) => {
      event.preventDefault();
      const text = event.clipboardData.getData("text/plain");
      document.execCommand("insertText", false, text);
      emitValue();
    },
    [emitValue]
  );

  return (
    <FormControl fullWidth={fullWidth} error={error}>
      <Box
        sx={{
          mb: 0.75,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <FormLabel
          sx={{
            color: error ? "error.main" : "text.primary",
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          {label}
        </FormLabel>
        <IconButton
          size="small"
          onMouseDown={(event) => event.preventDefault()}
          onClick={handleBold}
          aria-label={`${label} bold`}
          sx={{
            border: "1px solid",
            borderColor: error ? "error.main" : "divider",
            borderRadius: 1,
          }}
        >
          <FormatBoldIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box
        sx={{
          border: "1px solid",
          borderColor: error ? "error.main" : "rgba(0, 0, 0, 0.23)",
          borderRadius: 1,
          px: 1.75,
          py: 1.5,
          transition: "border-color 120ms ease, box-shadow 120ms ease",
          "&:focus-within": {
            borderColor: error ? "error.main" : "primary.main",
            boxShadow: (theme) =>
              `0 0 0 2px ${
                error ? theme.palette.error.light : theme.palette.primary.light
              }33`,
          },
        }}
      >
        <Box
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-label={label}
          data-placeholder={placeholder || label}
          onInput={emitValue}
          onBlur={() => {
            emitValue();
            onBlur?.();
          }}
          onPaste={handlePaste}
          sx={{
            minHeight,
            outline: "none",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            lineHeight: 1.5,
            fontSize: 16,
            "&:empty:before": {
              content: "attr(data-placeholder)",
              color: "text.disabled",
            },
            "& p, & div": {
              m: 0,
            },
            "& strong": {
              fontWeight: 700,
            },
          }}
        />
      </Box>

      <FormHelperText>{helperText || " "}</FormHelperText>
    </FormControl>
  );
};

export default RichTextField;
